const TWEMOJI_BASE = '/twemoji/'
const RASTER_SIZE = 1024

export interface TwemojiAsset {
  image: HTMLImageElement
  inner: string
}

const ZWJ = '‍'
const VARIATION_SELECTOR_RE = /️/g
const SVG_OPEN_RE = /^[\s\S]*?<svg\b[^>]*>/i
const SVG_CLOSE_RE = /<\/svg>\s*$/i

const emojiCodePoint = (emoji: string): string => {
  const cleaned = emoji.indexOf(ZWJ) < 0 ? emoji.replace(VARIATION_SELECTOR_RE, '') : emoji

  return Array.from(cleaned, (char) => char.codePointAt(0)?.toString(16) ?? '').join('-')
}

export const twemojiAssetUrl = (emoji: string): string => `${TWEMOJI_BASE}${emojiCodePoint(emoji)}.svg`

const cache = new Map<string, Promise<TwemojiAsset | null>>()

const svgToDataUrl = (svg: string): string => {
  const sized = svg.replace(/<svg\b/i, `<svg width="${RASTER_SIZE}" height="${RASTER_SIZE}"`)

  return `data:image/svg+xml;base64,${btoa(String.fromCharCode(...new TextEncoder().encode(sized)))}`
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()

    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Twemoji image failed to load'))
    image.src = src
  })

const fetchTwemoji = async (emoji: string): Promise<TwemojiAsset | null> => {
  try {
    const response = await fetch(twemojiAssetUrl(emoji))

    if (!response.ok) {
      return null
    }

    const svg = await response.text()
    const inner = svg.replace(SVG_OPEN_RE, '').replace(SVG_CLOSE_RE, '')
    const image = await loadImage(svgToDataUrl(svg))

    return { image, inner }
  } catch {
    return null
  }
}

export const loadTwemojiAsset = (emoji: string): Promise<TwemojiAsset | null> => {
  let promise = cache.get(emoji)

  if (!promise) {
    promise = fetchTwemoji(emoji)
    cache.set(emoji, promise)
  }

  return promise
}
